import React, { useEffect, useState } from 'react';

interface LightboxProps {
  imageUrl: string;
  altText: string;
  isOpen: boolean;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ imageUrl, altText, isOpen, onClose }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  
  // Close lightbox when Escape key is pressed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling when lightbox is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, isZoomed, onClose]);
  
  const handleBackgroundClick = () => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      onClose();
    }
  };
  
  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4 cursor-zoom-out"
      onClick={handleBackgroundClick}
    >
      <div className={`relative ${isZoomed ? 'w-full h-full' : 'max-w-screen-xl max-h-screen-90'} overflow-auto`}>
        {/* Close button */}
        <button 
          className="absolute top-4 right-4 bg-white bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition-all z-30 shadow-lg"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <svg className="h-6 w-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Zoom indicator */}
        <button 
          className="absolute top-4 left-4 bg-white bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition-all z-30 shadow-lg"
          onClick={(e) => {
            e.stopPropagation();
            setIsZoomed(!isZoomed);
          }}
        >
          <svg 
            className="h-6 w-6 text-gray-800" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            {isZoomed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m4-3h-6" />
            )}
          </svg>
        </button>
        
        {/* Image */}
        <div 
          className="flex items-center justify-center h-full"
          onClick={handleImageClick}
        >
          <img 
            src={imageUrl} 
            alt={altText} 
            className={`
              ${isZoomed 
                ? 'max-w-none w-auto h-auto cursor-zoom-out object-cover transform transition-transform duration-300' 
                : 'max-w-full max-h-[90vh] object-contain cursor-zoom-in'}
            `} 
          />
        </div>
        
        {/* Instructions */}
        <div className="absolute bottom-4 left-0 right-0 text-center z-30 pointer-events-none">
          <p className="text-white text-sm bg-black bg-opacity-50 mx-auto inline-block px-3 py-1 rounded-full">
            {isZoomed 
              ? 'Click image to zoom out' 
              : 'Click image to zoom in'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Lightbox; 